import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Monitor, MonitorCheck, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { SectionSelect } from '@/components/staffAttendance/SectionSelect';
import { staffAttApi, kioskConfig, errMsg, fmtTime } from '@/services/staffAttendanceApi';

export const SectionDevices = ({ sections }) => {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [section, setSection] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [mine, setMine] = useState(kioskConfig.get());
  const load = useCallback(() => staffAttApi.kiosks().then(setRows).catch((e) => toast.error(errMsg(e, 'Failed to load devices'))), []);
  useEffect(() => { load(); }, [load]);

  const setup = async () => {
    if (!section) { toast.error('Pick the section for this device'); return; }
    setBusy(true);
    try {
      const k = await staffAttApi.createKiosk({ section, name });
      const cfg = { token: k.token, kiosk_id: k.kiosk_id, section: k.section, name: k.name, site_id: localStorage.getItem('active_site_id') || 'salem' };
      kioskConfig.set(cfg); setMine(cfg); setName('');
      toast.success(`This device is now the attendance device for ${k.section}`);
      load();
    } catch (e) { toast.error(errMsg(e, 'Could not set up the device')); } finally { setBusy(false); }
  };
  const remove = async (k) => {
    if (!window.confirm(`Remove "${k.name}"? It will stop accepting attendance.`)) return;
    try {
      await staffAttApi.removeKiosk(k.kiosk_id);
      if (mine?.kiosk_id === k.kiosk_id) { kioskConfig.clear(); setMine(null); }
      toast.success('Device removed'); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-4" data-testid="section-devices">
      <Card className="border-0 shadow-md"><CardContent className="p-5 space-y-3">
        <h3 className="font-semibold flex items-center gap-2"><Monitor className="w-4 h-4 text-amber-600" />Set up THIS device as a section device</h3>
        {mine ? (
          <div className="flex flex-wrap items-center gap-3 rounded-lg bg-emerald-50 border border-emerald-200 p-3" data-testid="device-this-configured">
            <MonitorCheck className="w-5 h-5 text-emerald-600" />
            <span className="text-sm">This device is set up for <b>{mine.section}</b> ({mine.name}).</span>
            <Button size="sm" className="ml-auto bg-emerald-600 hover:bg-emerald-700" onClick={() => navigate('/attendance/kiosk')} data-testid="device-open-kiosk">Open attendance screen</Button>
            <Button size="sm" variant="outline" onClick={() => { kioskConfig.clear(); setMine(null); }} data-testid="device-disconnect">Disconnect this device</Button>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            <SectionSelect sections={sections} value={section} onChange={setSection} allowAll={false} testId="device-section-select" />
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Device name, e.g. Packing Tablet" className="max-w-[240px] bg-white" data-testid="device-name-input" />
            <Button onClick={setup} disabled={busy} className="bg-amber-500 hover:bg-amber-600" data-testid="device-setup-btn">{busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Set up this device</Button>
          </div>
        )}
        <p className="text-xs text-gray-500">The device stays on the attendance screen; staff never log in on it. It accepts Staff IDs of its own section only. You can log out after setting it up.</p>
      </CardContent></Card>
      {!rows && <Loader2 className="w-6 h-6 animate-spin mx-auto text-amber-600" />}
      <div className="grid gap-3 sm:grid-cols-2">
        {(rows || []).map((k) => (
          <Card key={k.kiosk_id} className="border-0 shadow" data-testid={`device-row-${k.kiosk_id}`}>
            <CardContent className="p-4 flex items-center gap-3">
              <Monitor className="w-7 h-7 text-slate-500" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{k.name}</div>
                <div className="text-xs text-gray-500">{k.section} · set up by {k.created_by_name}</div>
                <div className="text-[11px] text-gray-400">{k.last_seen_at ? `Last used ${k.last_seen_at.slice(0, 10)} ${fmtTime(k.last_seen_at)}` : 'Not used yet'}</div>
              </div>
              <Button size="icon" variant="ghost" onClick={() => remove(k)} className="text-red-500" data-testid={`device-remove-${k.kiosk_id}`}><Trash2 className="w-4 h-4" /></Button>
            </CardContent>
          </Card>
        ))}
      </div>
      {rows && rows.length === 0 && <p className="text-center text-gray-400" data-testid="devices-empty">No section devices yet.</p>}
    </div>
  );
};

export default SectionDevices;
